/**
 * @module View
 *
 * @returns {undefined}
 */
import { isString, toKey, setStyle, restoreStyle, idGen, show, hide, remove, toHtmlElem, } from "@ideal-postcodes/jsutil";
import { create } from "./state";
import { InterpreterStatus } from "@xstate/fsm";
import { announcer } from "./announcer";
/**
 * # View
 *
 * Represents the user interface which binds to the DOM.
 *
 * The View class is designed to be consumed by an
 * `Autocomplete.Controller` instance and should not be accessed directly
 * unless absolutely necessary.
 *
 * The main function of the View is to:
 * - Present suggestions supplied by the controller
 * - Provide callbacks to the controller for various user interactions
 * - Provide methods to manipulate the user interface if required
 */
export class View {
    /**
     * Creates an View instance
     */
    constructor(options) {
        this.options = options;
        this.ids = idGen("idpcaf");
        const { inputField } = options;
        // Configure container
        this.container = this.options.document.createElement("div");
        this.container.className = this.options.containerClass;
        this.container.id = this.ids();
        this.container.setAttribute("aria-haspopup", "listbox");
        // Create message element
        this.message = this.options.document.createElement("li");
        this.message.textContent = this.options.msgInitial;
        this.message.className = "idpc_error";
        // Configure UL
        this.list = this.options.document.createElement("ul");
        this.list.className = this.options.listClass;
        this.list.id = this.ids();
        this.list.setAttribute("aria-label", this.options.msgList);
        this.list.setAttribute("role", "listbox");
        hide(this.list);
        //configure unhide
        this.unhideEvent = this.unhideFields.bind(this);
        this.unhide = this.createUnhide();
        // Configure input
        if (isString(inputField)) {
            this.input = this.options.scope.querySelector(inputField);
        }
        else {
            this.input = inputField;
        }
        this.input.setAttribute("autocomplete", this.options.autocomplete);
        this.input.setAttribute("aria-autocomplete", "list");
        this.input.setAttribute("aria-controls", this.list.id);
        this.input.setAttribute("aria-autocomplete", "list");
        this.input.setAttribute("aria-activedescendant", "");
        this.input.setAttribute("autocorrect", "off");
        this.input.setAttribute("autocapitalize", "off");
        this.input.setAttribute("spellcheck", "false");
        if (!this.input.id)
            this.input.id = this.ids();
        // Apply additional accessibility improvments
        this.ariaAnchor().setAttribute("role", "combobox");
        this.ariaAnchor().setAttribute("aria-expanded", "false");
        this.ariaAnchor().setAttribute("aria-owns", this.list.id);
        this.inputListener = _onInput(this);
        this.blurListener = _onBlur(this);
        this.focusListener = _onFocus(this);
        this.keydownListener = _onKeyDown(this);
        this.mousedownListener = _onMousedown(this);
        const { container, announce } = announcer({
            idA: this.ids(),
            idB: this.ids(),
            document: this.options.document,
        });
        this.announce = announce;
        this.alerts = container;
        this.inputStyle = setStyle(this.input, this.options.inputStyle);
        setStyle(this.container, this.options.containerStyle);
        setStyle(this.list, this.options.listStyle);
        this.fsm = create({ view: this });
    }
    /**
     * Adds View to DOM
     * - Wraps input with container
     * - Appends suggestion list to container
     * - Enables listeners
     * - Starts FSM
     */
    attach() {
        if (this.fsm.status === InterpreterStatus.Running)
            return this;
        this.input.addEventListener("input", this.inputListener);
        this.input.addEventListener("blur", this.blurListener);
        this.input.addEventListener("focus", this.focusListener);
        this.input.addEventListener("keydown", this.keydownListener);
        this.list.addEventListener("mousedown", this.mousedownListener);
        const parent = this.input.parentNode;
        if (parent) {
            // Wrap input in a div and append suggestion list
            parent.insertBefore(this.container, this.input);
            this.container.appendChild(this.input);
            this.container.appendChild(this.list);
            this.container.appendChild(this.alerts);
            if (this.options.hide.length > 0 && this.options.unhide == null)
                this.container.appendChild(this.unhide);
        }
        this.fsm.start();
        this.options.onMounted.call(this);
        this.hideFields();
        return this;
    }
    /**
     * Removes View from DOM
     * - Disable listeners
     * - Removes sugestion list from container
     * - Appends suggestion list to container
     * - Enables listeners
     * - Stops FSM
     */
    detach() {
        if (this.fsm.status !== InterpreterStatus.Running)
            return this;
        this.input.removeEventListener("input", this.inputListener);
        this.input.removeEventListener("blur", this.blurListener);
        this.input.removeEventListener("focus", this.focusListener);
        this.input.removeEventListener("keydown", this.keydownListener);
        this.list.removeEventListener("mousedown", this.mousedownListener);
        this.container.removeChild(this.list);
        this.container.removeChild(this.alerts);
        const parent = this.container.parentNode;
        if (parent) {
            parent.insertBefore(this.input, this.container);
            parent.removeChild(this.container);
        }
        this.unmountUnhide();
        this.unhideFields();
        this.fsm.stop();
        restoreStyle(this.input, this.inputStyle);
        this.options.onRemove.call(this);
        return this;
    }
    /**
     * Sets message as a list item, no or empty string removes any message
     */
    setMessage(message) {
        this.fsm.send({ type: "NOTIFY", message });
        return this;
    }
    /**
     * Returns HTML Element which recevies key aria attributes
     *
     * @hidden
     */
    ariaAnchor() {
        if (this.options.aria === "1.0")
            return this.input;
        return this.container;
    }
    /**
     * Returns current address query
     */
    query() {
        return this.input.value;
    }
    /**
     * Current list of suggestions stored in view
     */
    suggestions() {
        return this.fsm.state.context.suggestions;
    }
    /**
     * Index of currently highlighted suggestion
     */
    current() {
        return this.fsm.state.context.current;
    }
    /**
     * Set address finder suggestions
     */
    setSuggestions(suggestions, query) {
        if (query !== this.query())
            return this;
        if (suggestions.length === 0)
            return this.setMessage(this.options.msgNoMatch);
        this.fsm.send({ type: "SUGGEST", suggestions });
        return this;
    }
    /**
     * Close address finder
     */
    close(reason) {
        this.fsm.send({ type: "CLOSE", reason });
        return this;
    }
    /**
     * Open address finder
     */
    open() {
        this.fsm.send("AWAKE");
        return this;
    }
    /**
     * Sets next suggestion as current
     */
    next() {
        this.fsm.send("NEXT");
        return this;
    }
    /**
     * Sets previous suggestion as current
     */
    previous() {
        this.fsm.send("PREVIOUS");
        return this;
    }
    /**
     * Given a HTMLLiElement, scroll parent until it is in view
     *
     * @hidden
     */
    scrollToView(li) {
        const liOffset = li.offsetTop;
        const ulScrollTop = this.list.scrollTop;
        if (liOffset < ulScrollTop) {
            this.list.scrollTop = liOffset;
        }
        const ulHeight = this.list.clientHeight;
        const liHeight = li.clientHeight;
        if (liOffset + liHeight > ulScrollTop + ulHeight) {
            this.list.scrollTop = liOffset - ulHeight + liHeight;
        }
        return this;
    }
    /**
     * Moves currently selected li into view
     *
     * @hidden
     */
    goto(i) {
        const lis = this.list.children;
        const suggestion = lis[i];
        if (i > -1 && lis.length > 0) {
            this.scrollToView(suggestion);
        }
        else {
            this.scrollToView(lis[0]);
        }
        return this;
    }
    /**
     * Trigger select event on an element or clicked HTMLElement
     */
    select(li) {
        let suggestion;
        if (li) {
            let i;
            let curr = li;
            for (i = 0; (curr = curr.previousElementSibling); i += 1)
                ;
            suggestion = this.suggestions()[i];
        }
        else {
            suggestion = this.suggestions()[this.current()];
        }
        if (suggestion)
            this.fsm.send({ type: "SELECT", suggestion });
        return this;
    }
    /**
     * Returns true if address finder is open
     */
    opened() {
        return !this.closed();
    }
    /**
     * Returs false if address finder is closed
     */
    closed() {
        return this.fsm.state.matches("closed");
    }
    /**
     * Creates a clickable element that can trigger unhiding of fields
     */
    createUnhide() {
        const e = findOrCreate(this.options.scope, this.options.unhide, () => {
            const e = this.options.document.createElement("p");
            e.innerText = this.options.msgUnhide;
            e.setAttribute("role", "button");
            e.setAttribute("tabindex", "0");
            if (this.options.unhideClass)
                e.className = this.options.unhideClass;
            return e;
        });
        e.addEventListener("click", this.unhideEvent);
        return e;
    }
    /**
     * Removes unhide elem from DOM
     */
    unmountUnhide() {
        this.unhide.removeEventListener("click", this.unhideEvent);
        if (this.options.unhide == null && this.options.hide.length)
            remove(this.unhide);
    }
    hiddenFields() {
        return this.options.hide
            .map((e) => {
            if (isString(e))
                return toHtmlElem(this.options.scope, e);
            return e;
        })
            .filter((e) => e !== null);
    }
    /**
     * Hides fields marked for hiding
     */
    hideFields() {
        this.hiddenFields().forEach(hide);
    }
    /**
     * Unhides fields marked for hiding
     */
    unhideFields() {
        this.hiddenFields().forEach(show);
        this.options.onUnhide.call(this);
    }
}
/**
 * Event handler: Fires when focus moves away from input field
 * Triggers:
 * - `onBlur` callback
 * - Closes suggestion list
 *
 * @hidden
 */
const _onBlur = (self) => function (_) {
    self.options.onBlur.call(self);
    self.close("blur");
};
/**
 * Event handler: Fires when input field is focused
 * Triggers:
 * - `onFocus` callback
 * - render of suggestion list
 *
 * @hidden
 */
const _onFocus = (view) => function (_) {
    view.options.onFocus.call(view);
    view.open();
};
/**
 * Event handler: Fires when input is detected on input fiel
 * Triggers:
 * - `onInput` callback
 *
 * @hidden
 */
const _onInput = (view) => function (event) {
    view.fsm.send({ type: "INPUT", event });
};
/**
 * Event handler: Fires when mousedown on `<li>` HTML Entity
 * Triggers:
 * - Selection of address suggestion
 *
 * @hidden
 */
const _onMousedown = (view) => function (event) {
    view.options.onMouseDown.call(view, event);
    const ul = view.list;
    let li = event.target;
    if (li !== ul) {
        while (li && !/li/i.test(li.nodeName)) {
            li = li.parentNode;
        }
        if (li && event.button === 0) {
            // Only select on left click
            event.preventDefault();
            view.select(li);
        }
    }
};
/**
 * Event handler: Fires on "keyDown" event of search field
 * Triggers:
 * - `select` selection of address if key is "Enter"
 * - `onInput` callback if key is "Backspace"
 * - `close` closing of suggestion list if key is "Esc"
 * - `next` highlight next suggestion if key is "Down"
 * - `previous` highlight next suggestion if key is "Up"
 *
 * @hidden
 */
export const _onKeyDown = (view) => function (event) {
    const key = toKey(event);
    //always prevent On enter
    if (key === "Enter") {
        event.preventDefault();
    }
    view.options.onKeyDown.call(view, event);
    if (view.closed()) {
        view.open();
        return;
    }
    if (view.fsm.state.matches("suggesting")) {
        if (key === "Enter") {
            view.select();
        }
        if (key === "Backspace")
            view.fsm.send({ type: "INPUT", event });
        if (key === "ArrowUp") {
            event.preventDefault();
            view.previous();
        }
        if (key === "ArrowDown") {
            event.preventDefault();
            view.next();
        }
    }
    if (key === "Escape")
        view.close("esc");
    if (key === "Home")
        view.fsm.send({ type: "RESET" });
    if (key === "End")
        view.fsm.send({ type: "RESET" });
};
/**
 * Retrieve Element
 * - If string, assumes is valid and returns first match within scope
 * - If null, invokes the create method to return a default
 * - If HTMLElement returns instance
 *
 * @hidden
 */
export const findOrCreate = (scope, q, create) => {
    if (isString(q))
        return scope.querySelector(q);
    if (create && q === null)
        return create();
    return q;
};
